import { useState, useEffect, memo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Box from '@mui/material/Box';
import ModifierForm from 'components/MenuNew/FormView/ModifierFormGroup/index';
import Preloader from 'components/Preloader';
import useQuery from 'hooks/useQuery';
import FormFooter from './FormFooter';
import {
  addModifier,
  deleteModifier,
  editModifierV2,
  getModifiersAll,
} from 'redux/actions/Menu/modifiers';
import FormHeader from 'pages/MenuNew/FormsView/NewModifierGroup/FormHeader';
import { confirm } from 'components/MenuNew/ConfirmModal';
import { makeStyles } from '@mui/styles';
import { getModifierDataById } from 'redux/actions/Menu/modifiers';
import { CONTEXT_TYPES } from 'constants/menu';
import Private from 'components/Private';
import { getMenusV2 } from 'redux/actions/Menu/menuV2';
import {
  addDefaultPriceOverride,
  buildProviderPriceOverrides,
} from 'utils/menu';
import ConfirmPromptModal from 'components/ConfirmPromptModal/ConfirmPromptModal';
import { useTranslation } from 'react-i18next';
import { SET_LOADING } from 'redux/actions/types';
import ChangeBasePriseDialog from 'components/ChangeBasePriseDialog';
import { set } from 'lodash';
import { NAME_ENUMS } from 'constants/products';

const optionPriceScore = (item) =>
  (Number(item?.price) || 0) +
  (item?.priceOverride || []).reduce(
    (sum, override) => sum + (Number(override?.price) || 0),
    0
  );

const dedupeModifierOptions = (list = []) => {
  const result = [];
  const indexById = new Map();
  const indexByName = new Map();

  list.forEach((item) => {
    const nameKey = String(item?.name || '')
      .trim()
      .toLowerCase();
    const existingIndex =
      (item?._id && indexById.has(item._id)
        ? indexById.get(item._id)
        : undefined) ??
      (nameKey && indexByName.has(nameKey)
        ? indexByName.get(nameKey)
        : undefined);

    if (existingIndex == null) {
      const index = result.length;
      result.push(item);
      if (item?._id) {
        indexById.set(item._id, index);
      }
      if (nameKey) {
        indexByName.set(nameKey, index);
      }
      return;
    }

    const existing = result[existingIndex];
    const preferCurrent =
      (optionPriceScore(item) > 0 && optionPriceScore(existing) === 0) ||
      (Boolean(item?._id) && !existing?._id) ||
      optionPriceScore(item) > optionPriceScore(existing);
    if (preferCurrent) {
      result[existingIndex] = item;
      if (item?._id) {
        indexById.set(item._id, existingIndex);
      }
      if (nameKey) {
        indexByName.set(nameKey, existingIndex);
      }
    }
  });

  return result;
};

const hasSizePricedOptions = (list) =>
  (Array.isArray(list) ? list : []).some((option) =>
    (option?.priceOverride || []).some(
      (override) => override?.context_type === CONTEXT_TYPES.modifierItem
    )
  );

const normalizeModifierFormState = (data) => {
  if (!data) {
    return data;
  }
  return {
    ...data,
    title: data.title || data.name,
    description: data.description || '',
    free: data.free || '',
    // The form saves this value back. Defaulting a size priced group to one
    // price when the API omits the flag turns off its size prices on save.
    isOnePrice:
      data.isOnePrice == null ? !hasSizePricedOptions(data.list) : data.isOnePrice,
    list: Array.isArray(data.list) ? dedupeModifierOptions(data.list) : data.list,
  };
};

const initialData = {
  min: null,
  max: null,
  free: '',
  canSelectMultipleOptions: false,
  maxTotalNumberOfModifierItems: '',
  minTotalNumberOfModifierItems: '',
  minAnySingleModifierItems: '',
  maxAnySingleModifierItems: '',
  name: '',
  title: '',
  description: '',
  list: [
    {
      name: '',
      price: 0,
      priceOverride: [],
      optionSort: 0,
    },
  ],
  isActive: true,
  internalName: '',
  isOnePrice: true,
};

const useStyles = makeStyles((theme) => ({
  preloader: {
    position: 'fixed !important',
  },
}));

function compare(a, b) {
  if (a.sort < b.sort) {
    return -1;
  }
  if (a.sort > b.sort) {
    return 1;
  }
  return 0;
}

const FormData = ({
  setCurrentModifier = () => {},
  currentModifier,
  currentProduct,
  modifierMethods,
  setTabValue,
  currentCategory,
  setTitleModifier = () => {},
  isDirty,
  setIsDirty,
  setModifierFormState,
  formErrors,
  updateFormErrors = () => {},
  isChanged,
  setIsChanged,
  handleErrors = () => {},
  loadingDuplicate,
  setLoadingDuplicate,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const isInOverview = history.location.pathname.includes('/overview');
  const isInModifierGroupPage = history.location.pathname.includes(
    '/menu/modifierGroups'
  );
  const [loading, setLoading] = useState(false);
  const { menu, changeBasePriceData } = useSelector(({ menu }) => menu);
  const query = useQuery();
  const mode = query.get('mode');
  const productId = query.get('pid');
  const categoryId = query.get('cid');
  const modifierId = query.get('mid');
  const entityType = query.get('entityType');
  const isInItemMotAdd =
    history.location.pathname.includes('/items') && entityType === 'modifier';

  const [formState, updateFormState] = useState(initialData);
  const [changed, setChanged] = useState(false);
  const [savedListVersion, setSavedListVersion] = useState(0);
  const isSubmittingRef = useRef(false); // Track if form submission is in progress to prevent duplicate submissions

  const applyModifierFormState = (data, { markSaved = false } = {}) => {
    const next = normalizeModifierFormState(data);
    if (!next || !Array.isArray(next.list)) {
      return false;
    }
    updateFormState(next);
    setTitleModifier(next.name);
    setCurrentModifier(next);
    if (markSaved) {
      setSavedListVersion((version) => version + 1);
    }
    return true;
  };

  useEffect(() => {
    if (mode === 'edit') {
      setLoading(true);
      dispatch(getModifierDataById(modifierId))
        .then((res) => {
          applyModifierFormState(res);
          if (setIsDirty) {
            setIsDirty(false);
          }
          if (setIsChanged) {
            setIsChanged(false);
          }
        })
        .finally(() => {
          dispatch({ type: SET_LOADING, payload: false });
          setLoading(false);
        });
    } else {
      const connectedProviders = isInModifierGroupPage
        ? menu.connectedProviders
        : currentProduct?.connectedProviders;
      const freshPriceOverride = buildProviderPriceOverrides(
        connectedProviders,
        CONTEXT_TYPES
      );
      const freshInitialData = {
        ...initialData,
        list: [{ ...initialData.list[0], priceOverride: freshPriceOverride }],
      };
      setTitleModifier('');
      setCurrentModifier(freshInitialData);
      updateFormState(freshInitialData);
    }
  }, [modifierId]);

  useEffect(() => {
    if (isInOverview || productId) {
      setModifierFormState({ ...formState });
    }
  }, [formState]);

  const validateModifiersMinMax = (formStateData) => {
    const isZero = (value) => value === 0;
    let copyFormErrors = { ...formErrors };
    let hasError = false;
    let hasErrorMax = false;

    const checkEmptyOnlyZero = (fieldValue, errorName, errorMessage) => {
      if (isZero(fieldValue)) {
        copyFormErrors = set(copyFormErrors, errorName, {
          error: true,
          message: errorMessage,
        });
        hasError = true;
      }
    };
    const checkEmptyOnlyZeroMax = (fieldValue, errorName, errorMessage) => {
      if (isZero(fieldValue)) {
        copyFormErrors = set(copyFormErrors, errorName, {
          error: true,
          message: errorMessage,
        });
        hasErrorMax = true;
      }
    };
    checkEmptyOnlyZero(formStateData?.min, NAME_ENUMS.min, 'menu.min_number');
    checkEmptyOnlyZeroMax(
      formStateData?.max,
      NAME_ENUMS.max,
      'menu.min_number'
    );
    if (hasError || hasErrorMax) {
      updateFormErrors({ ...copyFormErrors });
      return false;
    }

    return true;
  };

  const validateModifiers = (formStateData) => {
    const isEmptyWithZero = (value) =>
      value === undefined ||
      value === null ||
      value === '' ||
      Number(value) === 0;
    const isEmpty = (value) =>
      value === undefined || value === null || value === '';
    const isZero = (value) => value === 0;

    let copyFormErrors = { ...formErrors };
    let hasError = false;
    let hasEmptyError = false;
    let hasLastError = false;
    let hasMostError = false;

    // Check for empty values where zero is considered invalid
    const checkEmptyWithZero = (fieldValue, errorName, errorMessage) => {
      if (isEmptyWithZero(fieldValue)) {
        copyFormErrors = set(copyFormErrors, errorName, {
          error: true,
          message: errorMessage,
        });
        hasError = true;
      }
    };
    const checkEmptyOnlyZero = (fieldValue, errorName, errorMessage) => {
      if (isZero(fieldValue)) {
        copyFormErrors = set(copyFormErrors, errorName, {
          error: true,
          message: errorMessage,
        });
        hasLastError = true;
      }
    };

    const checkEmptyOnlyZeroMax = (fieldValue, errorName, errorMessage) => {
      if (isZero(fieldValue)) {
        copyFormErrors = set(copyFormErrors, errorName, {
          error: true,
          message: errorMessage,
        });
        hasMostError = true;
      }
    };

    // Check for empty values where zero is not considered invalid
    const checkEmpty = (fieldValue, errorName, errorMessage) => {
      if (isEmpty(fieldValue)) {
        copyFormErrors = set(copyFormErrors, errorName, {
          error: true,
          message: errorMessage,
        });
        hasEmptyError = true;
      }
    };

    // Validate fields
    checkEmpty(
      formStateData?.minTotalNumberOfModifierItems,
      NAME_ENUMS.minT,
      'This field is required'
    );
    checkEmptyWithZero(
      formStateData?.maxTotalNumberOfModifierItems,
      NAME_ENUMS.maxT,
      'menu.min_number'
    );

    checkEmptyOnlyZero(formStateData?.min, NAME_ENUMS.min, 'menu.min_number');
    checkEmptyOnlyZeroMax(
      formStateData?.max,
      NAME_ENUMS.max,
      'menu.min_number'
    );
    checkEmptyWithZero(
      formStateData?.maxAnySingleModifierItems,
      NAME_ENUMS.maxAnySingle,
      'menu.min_number'
    );
    checkEmpty(
      formStateData?.minAnySingleModifierItems,
      NAME_ENUMS.minAnySingle,
      'This field is required'
    );

    if (hasError || hasLastError || hasMostError || hasEmptyError) {
      updateFormErrors({ ...copyFormErrors });
      return false;
    }

    return true;
  };

  const handleValidSubmit = (val, e) => {
    if (changeBasePriceData?.changeBasePrice) {
      if (e) {
        e?.preventDefault();
      } else if (val) {
        val?.preventDefault();
      }
      return;
    }

    // Prevent duplicate submissions if already submitting
    if (isSubmittingRef.current) {
      if (e) {
        e?.preventDefault();
      } else if (val) {
        val?.preventDefault();
      }
      return;
    }

    // Set submitting flag immediately to prevent duplicate submissions
    // This must be set before any validation to prevent race conditions
    isSubmittingRef.current = true;

    if (setIsDirty) {
      setIsDirty(false);
    }
    if (e) {
      e?.preventDefault();
    } else if (val) {
      val?.preventDefault();
    }
    if (formState?.canSelectMultipleOptions) {
      if (formState?.minAnySingleModifierItems === '0') {
        formState.minAnySingleModifierItems = 0;
      }
      const isValidOption = validateModifiers(formState);
      if (!isValidOption) {
        isSubmittingRef.current = false; // Reset flag if validation fails
        return;
      }
    } else {
      const isValidOption = validateModifiersMinMax(formState);
      if (!isValidOption) {
        isSubmittingRef.current = false; // Reset flag if validation fails
        return;
      }
    }

    const priceError =
      formErrors?.list?.filter((item) => item?.price?.error) || [];
    const priceOverrideError =
      formErrors?.list?.filter((item) => {
        const findErr = item?.priceOverride?.find((priceOverride) => {
          return priceOverride?.price?.error;
        });
        if (findErr) {
          return findErr;
        }
      }) || [];

    if (
      handleErrors(formState) ||
      priceError.length ||
      formErrors?.['title']?.error
    ) {
      isSubmittingRef.current = false; // Reset flag if validation fails
      return;
    }
    const isError =
      formErrors && Object.values(formErrors)?.find((err) => err.error)?.error;

    if (isError || priceOverrideError?.length) {
      isSubmittingRef.current = false; // Reset flag if validation fails
      return;
    }
    formState.list?.forEach((item, index) => {
      item.optionSort = index;
      item.priceOverride?.forEach((modItem) => {
        if (modItem.context_type === CONTEXT_TYPES.provider) {
          if (modItem.pickupPrice == null || modItem.pickupPrice === '') {
            modItem.pickupPrice = modItem.price;
          }
          if (!Object.prototype.hasOwnProperty.call(modItem, 'locked')) {
            modItem.locked = true;
          }
        }
        if (modItem.context_type === 'MOD_ITEM') {
          modItem.locked = false;
          modItem.pickupPrice = modItem.price;
          modItem?.priceOverride?.forEach((modPriceOverride) => {
            if (
              modPriceOverride.pickupPrice == null ||
              modPriceOverride.pickupPrice === ''
            ) {
              modPriceOverride.pickupPrice = modPriceOverride.price;
            }
            if (
              !Object.prototype.hasOwnProperty.call(modPriceOverride, 'locked')
            ) {
              modPriceOverride.locked = true;
            }
          });
        }
      });
    });
    // Continue with submission (flag already set above)
    setLoading(true);
    if (mode != 'edit') {
      dispatch(addModifier({ ...formState }))
        .then((res) => {
          if (!isInModifierGroupPage) {
            goToModifierOptions(res);
          } else {
            if (isInOverview) {
              history.push({
                search: `entityType=modifier&mid=${res._id}&mode=edit`,
              });
            } else {
              history.push({
                search: ``,
              });
            }

            if (setIsDirty) {
              setIsDirty(false);
            }
            if (setIsChanged) {
              setIsChanged(false);
            }
          }
        })
        .finally(() => {
          // reset(values);
          setLoading(false);
          isSubmittingRef.current = false; // Reset submission flag
        });
    } else {
      addDefaultPriceOverride(currentCategory, menu, CONTEXT_TYPES, formState);
      dispatch(editModifierV2({ ...formState }))
        .then((res) => {
          if (setIsDirty) {
            setIsDirty(false);
          }
          if (setIsChanged) {
            setIsChanged(false);
          }
          const syncSavedModifier = () => {
            if (applyModifierFormState(res, { markSaved: true })) {
              return Promise.resolve(res);
            }
            return dispatch(
              getModifierDataById(res?._id || formState._id || modifierId)
            ).then((fresh) => {
              applyModifierFormState(fresh, { markSaved: true });
              return fresh || res;
            });
          };
          return syncSavedModifier().then((saved) => {
            // Do not POST /modifier/group here. Adding the group to the item
            // form leaves Update enabled so the user can assign it.
            goToModifierOptions(saved);
          });
        })
        .finally(() => {
          // reset(values);
          setLoading(false);
          isSubmittingRef.current = false; // Reset submission flag
        });
    }
  };

  const goToModifierOptions = (res = null) => {
    const isError =
      formErrors && Object.values(formErrors)?.find((err) => err.error)?.error;
    if (isError) {
      return;
    }

    if (!isInModifierGroupPage) {
      const exist = modifierMethods.fields.findIndex(
        (item) => item?._id === res?._id
      );
      if (exist !== -1) {
        modifierMethods.update(exist, res);
      } else {
        modifierMethods.append(res);
      }
      setTabValue?.(1);
      history.push({
        pathname: history.location.pathname || '/menu/overview',
        search: `?entityType=item&pid=${productId}&cid=${categoryId}&mode=edit`,
        state: { goBack: true },
      });
    } else {
      history.push({
        search: `entityType=modifier&mid=${
          res?._id || currentModifier?._id
        }&mode=edit`,
        state: { create: true },
      });
    }
    setChanged(false);
  };

  // const changeIsOnePrice = (checked) => {
  //   updateFormState({ ...formState, isOnePrice: !checked });
  // };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'confirm_modal.delete_modifier',
      message: 'settings.are_you_sure_delete_modifier_group_add_to',
      cancelText: t('orders.cancel'),
      confirmText: t('table_ordering.yes_delete'),
      confirmColor: 'primary',
    });
    if (confirmed) {
      setLoading(true);
      dispatch(deleteModifier(modifierId)).finally(() => {
        history.push('/menu/modifierGroups');
        // reset(initialData);
        setLoading(false);
        dispatch(getMenusV2());
      });
      dispatch(getModifiersAll());
    }
  };

  const handleCancel = () => {
    if (setIsDirty) {
      setIsDirty(false);
    }
    if (setIsChanged) {
      setIsChanged(false);
    }
    setChanged(false);

    const currentPath = history.location.pathname;
    const fallbackPath = currentPath
      ? currentPath
      : isInModifierGroupPage
      ? '/menu/modifierGroups'
      : isInOverview
      ? '/menu/overview'
      : '/menu/items';

    if (productId && !isInModifierGroupPage) {
      history.push({
        pathname: fallbackPath,
        search: `?entityType=item&pid=${productId}&cid=${categoryId}&mode=edit`,
        state: { cancel: true, goBack: true },
      });
      return;
    }

    history.push({
      pathname: fallbackPath,
      search: isInModifierGroupPage ? '' : history.location.search,
      state: { cancel: true, goBack: true },
    });
  };

  return (
    <form
      style={{ height: 'inherit' }}
      onSubmit={handleValidSubmit}
      onKeyDown={(e) => {
        // Prevent form submission via Enter key if already submitting or loading
        if (e.key === 'Enter' && (isSubmittingRef.current || loading)) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <ConfirmPromptModal
        handleConfirm={() => {
          return handleValidSubmit();
        }}
        notHookForm={true}
        submitData={formState}
        hasUnsavedChanges={isDirty}
        isFormError={formErrors}
      />

      {(loading || loadingDuplicate) && (
        <Preloader overlay={true} className={classes.preloader} />
      )}
      {isInModifierGroupPage ? (
        <Box>
          <FormHeader
            currentProduct={currentProduct}
            currentModifier={currentModifier}
            title={formState.name}
            handleDelete={handleDelete}
            mode={mode}
            setLoadingDuplicate={setLoadingDuplicate}
          />
        </Box>
      ) : (
        <></>
      )}
      <Box
        sx={{ height: 'calc(100% - 174.5px)', overflowY: 'auto', px: 4, py: 3 }}
      >
        <Private permission="MENU_EDIT" disabled={true}>
          <ModifierForm
            isInOverview={isInOverview}
            isInModifierGroupPage={isInModifierGroupPage}
            mode={mode}
            // changeIsOnePrice={changeIsOnePrice}
            currentProduct={currentProduct}
            formState={formState}
            formErrors={formErrors}
            setIsDirty={setIsDirty}
            setIsChanged={setIsChanged}
            setChanged={setChanged}
            updateFormErrors={updateFormErrors}
            updateFormState={updateFormState}
            handleErrors={handleErrors}
            savedListVersion={savedListVersion}
          />
        </Private>
      </Box>
      <Box>
        <FormFooter
          isDisable={isDirty || isChanged || changed || loading}
          isInOverview={isInOverview}
          isInItemMotAdd={isInItemMotAdd}
          handleCancel={handleCancel}
          mode={mode}
        />
      </Box>
      <ChangeBasePriseDialog />
    </form>
  );
};

export default memo(FormData);
